import React from 'react';
import I81n from 'i18n-js';

import { BESTSELLER_SUBCATEGORIES_TO_EXCLUDE } from './shared/constants';
import { RCTooltip } from '@reverbdotcom/cadence/components';
import { Item2TitleDataFragment } from '@reverbdotcom/commons/src/gql/graphql';
import BestSellerRank from './best_seller_rank';

interface IProps {
  cspId: string,
  categories: Item2TitleDataFragment['categories'],
  listingId?: string,
  ssr?: boolean;
}

interface ISubcatRankings {
  [subcatId: string]: number;
}

function isTopSeller(
  subcategoryIds: string[],
  subcategoryRankings: ISubcatRankings,
  threshold: number,
) {
  return subcategoryIds.some(subcatId => subcategoryRankings[subcatId] > NOT_RANKED && subcategoryRankings[subcatId] <= threshold);
}

export const RANKING_THRESHOLD = 100;
export const BADGE_THRESHOLD = 25;

// The lowest rank that would result in any user seeing ranking or badges, regardless of the current user's grouping.
export const TRACKING_THRESHOLD = Math.max(RANKING_THRESHOLD, BADGE_THRESHOLD);

export const NOT_RANKED = 0;

export function BestSellerBadge(props: IProps) {
  const { categories, cspId, ssr } = props;

  const subcategories = categories?.filter(cat => cat.rootSlug !== '' && !BESTSELLER_SUBCATEGORIES_TO_EXCLUDE.includes(cat.slug)) ?? [];
  const subcategoryIds = subcategories.map(cat => cat.id);

  // verbose way to initialize { 'cat-uuid-1': null, 'cat-uuid-2': null } to make tsc happy
  const initialSubcategoryRankingMap = subcategories.reduce((ac, subcat) => ({ ...ac, [subcat.id]: null }), {}) as ISubcatRankings;
  const [subcategoryRankings, setSubcategoryRankings] = React.useState(initialSubcategoryRankingMap);

  const shouldDisplayBadge = isTopSeller(subcategoryIds, subcategoryRankings, BADGE_THRESHOLD);

  if (!cspId) { return null; }

  return (
    <div className="product-badge product-badge--best-seller">
      {shouldDisplayBadge && (
        <RCTooltip text={I81n.t('discovery.item2Layout.bestSellerBadge.tooltipText')} type="underline" placement="top">
          <span className="product-badge__badge">
            <span className="product-badge__text">
              {I81n.t('discovery.item2Layout.bestSellerBadge.bestSeller')}
            </span>
          </span>
        </RCTooltip>
      )}
      {subcategories.map(subcategory => {
        return (
          <BestSellerRank
            key={subcategory.id}
            cspId={cspId}
            subcategory={subcategory}
            subcategoryRankings={subcategoryRankings}
            onLoad={setSubcategoryRankings}
            bestSellerThreshold={TRACKING_THRESHOLD}
            ssr={ssr}
          />
        );
      })}
    </div>
  );
}

export default BestSellerBadge;
